import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, MapPin, Users, ArrowRight, Store } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://getbitsy.ai';

export default function VenuesIndexPage() {
  const [cities, setCities] = useState([]);
  const [top, setTop] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/venue-partners/leaderboard/cities`),
          axios.get(`${BACKEND_URL}/api/venue-partners/leaderboard?limit=12`),
        ]);
        setCities(c.data.cities || []);
        setTop(t.data.venues || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Venues using BHERMES verification',
    description: 'Public leaderboard of bars, clubs, events and restaurants using the BHERMES / getbitsy.ai Trusted Guest verification scanner.',
    numberOfItems: cities.length,
    itemListElement: cities.slice(0, 20).map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE}/venues/${c.slug}`,
      name: `${c.city}, ${c.country}`,
    })),
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid="venues-index-page">
      <SEO
        title="Venues using BHERMES verification — by city"
        description="Public leaderboard of bars, clubs, and events running the BHERMES Trusted Guest scanner. See which cities and venues are on the protocol — zero PII."
        canonical={`${SITE}/venues`}
        schema={itemListSchema}
      />
      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">← getbitsy.ai</Link>
        <Link to="/verify-badge/venue" className="text-xs tracking-widest text-amber-400 hover:text-amber-300">
          + Join the partner program
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 space-y-12">
        <section>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 mb-4">VENUE LEADERBOARD</Badge>
          <h1 className="text-5xl font-semibold leading-tight">
            Trusted by <span className="text-amber-400">real venues</span>.
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-3xl leading-relaxed">
            Bars, clubs, restaurants, and events running the BHERMES Trusted Guest scanner at the door.
            Every scan counts toward a guest's portable trust score — and toward the venue's ranking here.
          </p>
        </section>

        {loading && <p className="text-zinc-500 text-sm">Loading leaderboard…</p>}

        {!loading && cities.length === 0 && (
          <Card className="bg-zinc-900 border-zinc-800" data-testid="empty-state">
            <CardContent className="p-10 text-center space-y-3">
              <Store className="w-10 h-10 text-zinc-500 mx-auto" />
              <p className="text-zinc-300">Be the first venue on the leaderboard.</p>
              <Link to="/verify-badge/venue" className="text-amber-400 hover:underline text-sm">Register in under a minute →</Link>
            </CardContent>
          </Card>
        )}

        {cities.length > 0 && (
          <>
            {/* Cities grid */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-400" /> Cities
              </h2>
              <div className="grid md:grid-cols-3 gap-3" data-testid="cities-grid">
                {cities.map((c) => (
                  <Link key={c.slug} to={`/venues/${c.slug}`} data-testid={`city-card-${c.slug}`}>
                    <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition h-full">
                      <CardContent className="p-5 space-y-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-zinc-100">{c.city}</p>
                            <p className="text-xs text-zinc-500">{c.country}</p>
                          </div>
                          <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">
                            {c.venue_count} {c.venue_count === 1 ? 'venue' : 'venues'}
                          </Badge>
                        </div>
                        <div className="pt-2 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-400">
                          <span><Users className="inline w-3 h-3 mr-1" />{c.distinct_guests} guests</span>
                          <span>{c.scans} scans</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>

            {/* Global top venues */}
            {top.length > 0 && (
              <section>
                <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-400" /> Global top venues
                </h2>
                <div className="space-y-2" data-testid="global-top-list">
                  {top.map((v, i) => (
                    <div key={v.venue_id}
                      className="flex items-center gap-4 p-3 rounded-lg border border-zinc-800 bg-zinc-900/40"
                      data-testid={`global-venue-${v.venue_id}`}>
                      <span className={`text-2xl font-semibold w-8 ${i < 3 ? 'text-amber-400' : 'text-zinc-500'}`}>#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-100 truncate">{v.name}</p>
                        <p className="text-xs text-zinc-500">{v.city}, {v.country} · {v.type}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-zinc-100 font-mono">{v.distinct_guests}</p>
                        <p className="text-[10px] text-zinc-500 tracking-widest">GUESTS</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        <section className="border-t border-zinc-800 pt-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-widest text-zinc-500">FOR VENUES</p>
            <p className="text-lg text-zinc-100">Free forever. Your scans raise guest trust + your public ranking.</p>
          </div>
          <Link to="/verify-badge/venue">
            <Badge className="bg-amber-500 hover:bg-amber-600 text-black cursor-pointer px-4 py-2 text-sm">
              Join partner program <ArrowRight className="inline w-3 h-3 ml-1" />
            </Badge>
          </Link>
        </section>
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · <Link to="/" className="hover:text-zinc-400">getbitsy.ai</Link>
      </footer>
    </div>
  );
}
